import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Quotes, Calendar } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const navigate = useNavigate();

  const openDemoDialog = () => {
    navigate('/?demo=true');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ fontFamily: "'Arial', sans-serif" }} data-testid="about-page">
      {/* Purple Header Bar */}
      <header className="bg-[#3D1D72] text-white py-4 px-6 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity" data-testid="about-header-home-link">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-xl font-bold">H</span>
            </div>
            <span className="text-xl font-semibold">HERChain Solutions</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
            data-testid="about-back-home-link"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero — Eyebrow + Big Statement */}
        <section className="bg-gradient-to-b from-[#F8F5FF] to-white pt-16 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-sm font-semibold tracking-widest text-[#C9A84C] uppercase mb-4">
              About HERChain Solutions
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3D1D72] leading-tight tracking-tight mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="about-hero-heading"
            >
              Built by someone who's<br />actually done the job.
            </h1>
            <div className="w-16 h-1 bg-[#C9A84C] mb-2" />
          </div>
        </section>

        {/* Body Story */}
        <section className="px-6 py-12">
          <div className="max-w-3xl mx-auto space-y-8 text-gray-800 leading-relaxed text-lg">
            <p>
              <span className="font-semibold text-[#3D1D72]">HERChain Solutions was founded by Dahlia Lewis</span> — a Caribbean hospitality procurement professional with over <span className="font-semibold">23 years</span> of hands-on industry experience. Not as a consultant. Not as an outside observer. As the person sitting in the purchasing office, managing the vendors, chasing the freight, and trying to make legacy systems work for Caribbean realities they were never designed for.
            </p>

            <p>
              Dahlia has held senior procurement roles at some of the most prestigious properties in the region — including <span className="font-semibold">Four Seasons Resort Nevis</span> and <span className="font-semibold">Necker Island</span>, the private island estate of Sir Richard Branson. She has served as Purchasing Manager, Director of Inventory, and Director of Procurement, giving her a firsthand view of procurement challenges at every level of a hospitality operation.
            </p>

            <p>
              She was also part of the original implementation team for <span className="font-semibold">Birchstreet</span> at a major Caribbean hotel — and went on to serve as its system administrator. She knows exactly what enterprise procurement platforms can do. More importantly, she knows exactly where they fall short for Caribbean properties: the currency limitations, the freight complexity, the vendor dynamics, the workarounds that become permanent fixtures.
            </p>
          </div>
        </section>

        {/* Pull Quote */}
        <section className="px-6 py-12 bg-[#3D1D72] text-white">
          <div className="max-w-3xl mx-auto">
            <Quotes size={48} weight="fill" className="text-[#C9A84C] mb-6" />
            <blockquote
              className="text-2xl sm:text-3xl leading-relaxed font-medium mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="about-founder-quote"
            >
              "Every feature in HERChain exists because I've felt the pain of not having it. The landed cost calculator, the freight forwarder portal, the multi-currency support — these aren't features we thought sounded good. They're solutions to problems I dealt with personally, for over two decades."
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-12 h-0.5 bg-[#C9A84C]" />
              <p className="text-white/80 text-sm tracking-wide">
                <span className="font-semibold text-white">Dahlia Lewis</span>, Founder, HERChain Solutions
              </p>
            </div>
          </div>
        </section>

        {/* Closing Paragraphs */}
        <section className="px-6 py-12">
          <div className="max-w-3xl mx-auto space-y-8 text-gray-800 leading-relaxed text-lg">
            <p>
              HERChain Solutions was built specifically for the Caribbean hospitality market — with the regional currencies, supplier relationships, import logistics, and operational rhythms that define procurement in this part of the world <span className="italic">baked in from day one</span>. It is not a global platform adapted for the Caribbean. It is a Caribbean platform, full stop.
            </p>

            <p>
              Today, Dahlia continues to work as an active Inventory & Purchasing Manager — which means HERChain is continuously refined by someone <span className="font-semibold">still doing the job</span>, not someone who used to. When the industry changes, so does the platform.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 py-16 bg-[#F8F5FF]">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#3D1D72] mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Ready to see HERChain in action?
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Book a demo and speak directly with the founder — the person who built it and still uses the same processes every day.
            </p>
            <Button
              size="lg"
              className="bg-[#C9A84C] hover:bg-[#B8973D] text-white px-10 py-6 text-lg shadow-lg shadow-[#C9A84C]/30"
              onClick={openDemoDialog}
              data-testid="about-book-demo-btn"
            >
              <Calendar size={22} className="mr-2" />
              Book a Demo
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#3D1D72] text-white py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-white/70 text-sm">
          &copy; {new Date().getFullYear()} HERChain Solutions. Built for the Caribbean, by someone who's still there.
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
